import type { components } from "@majitap/sdk";
import { type LngLat, type MapZone, openRing, polygonOf, ringsOf } from "@majitap/ui";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";

type Schemas = components["schemas"];
export type Zone = Schemas["Geofence"];
export type ZoneLocation = Schemas["ZoneLocation"];

export const zonesListQuery = queryOptions({
  queryKey: ["geofences", "all"],
  queryFn: async () => (await ok(api.GET("/v1/admin/geofences"))).data,
});

/** Every place a zone holds (all pages): for the map, and to see who moved after a change. */
export async function allZoneLocations(id: string): Promise<ZoneLocation[]> {
  const out: ZoneLocation[] = [];
  let cursor: string | undefined;
  // at most 5,000 places: more than any zone holds today
  for (let page = 0; page < 50; page++) {
    const res = await ok(
      api.GET("/v1/admin/geofences/{id}/locations", { params: { path: { id }, query: { cursor, limit: 100 } } }),
    );
    out.push(...res.data);
    if (!res.next_cursor) break;
    cursor = res.next_cursor;
  }
  return out;
}

export const zoneLocationsQuery = (id: string) =>
  queryOptions({ queryKey: ["geofences", "locations", id], queryFn: () => allZoneLocations(id) });

/** What the map draws for a zone. */
export const toMapZone = (z: Zone): MapZone => ({
  id: z.id,
  name: z.name,
  rings: ringsOf(z.geometry),
  muted: z.status === "inactive",
});

/** The zone's first polygon as an editable outline (a zone drawn here has one). */
export const outlineOf = (z: Zone): LngLat[] => openRing(ringsOf(z.geometry)[0] ?? []);

type Geometry = Schemas["CreateGeofenceBody"]["geometry"];

/**
 * A zone's shape from pasted GeoJSON (a GIS export): a Polygon or MultiPolygon geometry, or a
 * Feature, or a FeatureCollection with one feature. Null when it isn't one; the API checks the rest.
 */
export function geometryFromGeoJson(text: string): Geometry | null {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return null;
  }
  const v = value as { type?: string; geometry?: unknown; features?: { geometry?: unknown }[]; coordinates?: unknown };
  const g = (
    v?.type === "FeatureCollection" && v.features?.length === 1
      ? v.features[0]?.geometry
      : v?.type === "Feature"
        ? v.geometry
        : v
  ) as { type?: string; coordinates?: unknown } | undefined;
  if ((g?.type === "Polygon" || g?.type === "MultiPolygon") && Array.isArray(g.coordinates))
    return g as unknown as Geometry;
  return null;
}

/**
 * Whether the map's editor can redraw a zone without losing anything: one outline and no holes.
 * A zone made of several parts, or with a hole, came from the API or the migration; redrawing
 * its first outline would drop the rest and move places out of it.
 */
export function shapeEditable(z: Pick<Zone, "geometry">): boolean {
  const g = z.geometry as { type?: string; coordinates?: unknown[][] } | null;
  if (g?.type === "Polygon") return (g.coordinates ?? []).length <= 1;
  if (g?.type === "MultiPolygon") return (g.coordinates ?? []).length === 1 && (g.coordinates?.[0] ?? []).length <= 1;
  return true;
}

export { polygonOf };

/**
 * Who moved after a zone changed: the places it held before and after the save. The database
 * re-matches every place in the same transaction (`relink_geofence`); this says which ones.
 */
export function movedSummary<T extends Pick<ZoneLocation, "id">>(before: T[], after: T[]) {
  const was = new Set(before.map((l) => l.id));
  const now = new Set(after.map((l) => l.id));
  return {
    movedIn: after.filter((l) => !was.has(l.id)),
    movedOut: before.filter((l) => !now.has(l.id)),
  };
}

export const zoneActions = {
  create: (body: Schemas["CreateGeofenceBody"]) => ok(api.POST("/v1/admin/geofences", { body })),
  update: (id: string, body: Schemas["UpdateGeofenceBody"]) =>
    ok(api.PATCH("/v1/admin/geofences/{id}", { params: { path: { id } }, body })),
  remove: (id: string) => ok(api.DELETE("/v1/admin/geofences/{id}", { params: { path: { id } } })),
};

// --- prices in zones and at places (P8-40) ----------------------------------------------------

export type ZoneSheet = Schemas["ZoneSheet"];
export type SheetCell = ZoneSheet["zones"][number]["items"][number];
export type PriceLine = Schemas["StaffQuoteLine"];
export type NearZone = Schemas["NearZone"];
export type StaffQuote = Schemas["StaffQuote"];

/** Places no active zone covers (up to 50): they can't be priced or ordered to. */
export const outsidePlacesQuery = queryOptions({
  queryKey: ["geofences", "outside"],
  queryFn: async () =>
    (await ok(api.GET("/v1/admin/locations", { params: { query: { outside_zones: "true", limit: 50 } } }))).data,
});

/** Every zone × item, priced as a place in that zone with no owner (needs orders.read). */
export const zoneSheetQuery = queryOptions({
  queryKey: ["pricing", "zone-sheet"],
  staleTime: 60_000,
  queryFn: async () => ok(api.GET("/v1/admin/pricing/zone-sheet")),
});

/** The zone a place at this point is in (the smallest active one covering it), and the nearest. */
export const zoneAtQuery = (lng: number, lat: number) =>
  queryOptions({
    queryKey: ["geofences", "at", lng, lat],
    queryFn: () => ok(api.GET("/v1/admin/geofences/at", { params: { query: { longitude: lng, latitude: lat } } })),
  });

/** A staff quote at a saved place, or for a zone with no owner. */
export const placeQuote = (
  at: { location_id: string } | { geofence_id: string },
  productItemId: string,
  quantity: number,
) =>
  ok(
    api.POST("/v1/admin/pricing/quote", {
      body: { ...at, items: [{ product_item_id: productItemId, quantity: String(quantity) }] },
    }),
  );

/** The cell of the sheet for one zone and item. */
export const cellOf = (sheet: ZoneSheet | undefined, zoneId: string | null | undefined, itemId: string | undefined) =>
  sheet?.zones.find((z) => z.geofence_id === zoneId)?.items.find((i) => i.product_item_id === itemId) ?? null;

/**
 * Where a price comes from, in words: the zone's own price, a contract, this place's, or the
 * price that applies in any zone. `own`: set for here, rather than the general price.
 */
export function sourceOf(scope: PriceLine["scope"], zoneName?: string | null): { label: string; own: boolean } {
  switch (scope) {
    case "zone":
      return { label: zoneName ?? "This zone", own: true };
    case "location":
      return { label: "This place", own: true };
    case "customer":
    case "business":
      return { label: "Contract", own: true };
    case "partner":
      return { label: "Partner rate", own: true };
    case "fillsite":
      return { label: "Fill site rate", own: true };
    default:
      return { label: "Any zone", own: false };
  }
}

/** One priced item's lines, split as people read them: the price, other charges, and costs. */
export function breakdown(lines: PriceLine[]) {
  return {
    price: lines.find((l) => l.charge_type.code === "product_price") ?? null,
    charges: lines.filter((l) => l.direction === "receivable" && l.charge_type.code !== "product_price"),
    costs: lines.filter((l) => l.direction === "payable"),
  };
}

/** "Wanjiku Kamau · Argwings Kodhek Rd" */
export const placeLine = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(" · ");

/** A place being looked at on the zones page: a saved place, an address, or a dropped pin. */
export type LookedAt =
  | {
      kind: "saved";
      id: string;
      label: string;
      detail: string | null;
      lng: number;
      lat: number;
      href: string | null;
      /** the zone it's in now, or null outside every zone */
      zoneName: string | null;
    }
  | { kind: "address" | "pin"; label: string; detail: string | null; lng: number; lat: number };

/** Saved places whose name, address or owner matches (for the place search). */
export async function findSavedPlaces(q: string): Promise<LookedAt[]> {
  const res = await ok(api.GET("/v1/admin/locations", { params: { query: { q, limit: 5 } } }));
  return res.data
    .filter((l) => l.longitude !== null && l.latitude !== null)
    .map((l) => ({
      kind: "saved" as const,
      id: l.id,
      label: placeLine(l.label, l.owner?.name),
      detail: l.address_line,
      zoneName: l.geofence?.name ?? null,
      lng: l.longitude as number,
      lat: l.latitude as number,
      href: l.owner ? `/${l.owner.type === "business" ? "businesses" : "customers"}/${l.owner.id}?tab=places` : null,
    }));
}

/** Address suggestions from the company's geocoding integration; one session per search. */
export const findAddresses = async (q: string, session: string) =>
  (await ok(api.GET("/v1/admin/locations/address-search", { params: { query: { q, session } } }))).data;

export const addressPoint = (placeId: string, session: string) =>
  ok(
    api.GET("/v1/admin/locations/address-search/{place_id}", {
      params: { path: { place_id: placeId }, query: { session } },
    }),
  );

/** "1.2 km" or "850 m" */
// number-ok: a distance in metres, not money
export const distanceText = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);

const places = (n: number) => (n === 1 ? "1 place" : `${n} places`);

/** "3 places moved in, 1 place moved out." or "No place changed zone." */
export function movedText(movedIn: unknown[], movedOut: unknown[]): string {
  if (!movedIn.length && !movedOut.length) return "No place changed zone.";
  const parts = [
    movedIn.length ? `${places(movedIn.length)} moved in` : null,
    movedOut.length ? `${places(movedOut.length)} moved out` : null,
  ].filter(Boolean);
  return `${parts.join(", ")}.`;
}

/** Whether a point is inside one of a zone's outlines (ray casting over the outer rings the map draws). */
export function inZone(at: LngLat, z: Pick<Zone, "geometry">): boolean {
  const [x, y] = at;
  return ringsOf(z.geometry).some((ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  });
}
